import * as React from 'react'
import { MoreHorizontal } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { EmptyState } from '@/components/ui/empty-state'
import { useViewOnly } from '@/components/templates/app-shell'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { deleteJSON, getJSON, postJSON } from '@/lib/api'
import type { ProfileGroup } from '@/lib/contacts/types'

interface GroupsResponse {
  groups: Array<ProfileGroup & { member_count: number }>
}

export function ContactGroupsTab({
  contactId,
  groups,
  profileQueryKey,
}: {
  contactId: number
  groups: ProfileGroup[]
  profileQueryKey: readonly unknown[]
}) {
  const [adding, setAdding] = React.useState(false)
  const [removing, setRemoving] = React.useState<ProfileGroup | null>(null)
  const viewOnly = useViewOnly()
  const queryClient = useQueryClient()
  const remove = useMutation({
    mutationFn: (groupId: number) => deleteJSON(`/api/v1/groups/${groupId}/members/${contactId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: profileQueryKey }),
  })

  return (
    <>
      {!viewOnly && (
        <div className="flex justify-end pb-3">
          <Button type="button" variant="outline" size="sm" onClick={() => setAdding(true)}>
            Add to Group
          </Button>
        </div>
      )}
      {remove.isError && <Alert className="mb-3">Group membership could not be removed.</Alert>}
      {groups.length === 0 ? (
        <EmptyState
          title="No Groups"
          description="Add this Contact to a hand-managed Group."
          className="items-start px-0 text-left"
        />
      ) : (
        <div className="overflow-hidden rounded-md border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Group</TableHead>
                {!viewOnly && <TableHead className="w-12" aria-label="Group actions" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {groups.map((group) => (
                <TableRow key={group.id}>
                  <TableCell>{group.name}</TableCell>
                  {!viewOnly && (
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 px-0"
                            aria-label={`${group.name} actions`}
                          >
                            <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => setRemoving(group)}>
                            Remove from Group
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <AddContactToGroupDialog
        open={adding}
        onOpenChange={setAdding}
        contactId={contactId}
        currentGroups={groups}
        onAdded={() => queryClient.invalidateQueries({ queryKey: profileQueryKey })}
      />
      <Dialog
        open={removing !== null}
        onOpenChange={(open) => {
          if (!open) setRemoving(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove from Group?</DialogTitle>
            <DialogDescription>
              This Contact will no longer belong to {removing?.name}. The Group and Contact remain
              available.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setRemoving(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => {
                if (!removing) return
                remove.mutate(removing.id, { onSuccess: () => setRemoving(null) })
              }}
            >
              {remove.isPending ? 'Removing…' : 'Remove from Group'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function AddContactToGroupDialog({
  open,
  onOpenChange,
  contactId,
  currentGroups,
  onAdded,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  contactId: number
  currentGroups: ProfileGroup[]
  onAdded: () => Promise<unknown>
}) {
  const [groupId, setGroupId] = React.useState('')
  const groupsQuery = useQuery({
    queryKey: ['contact-groups'],
    queryFn: () => getJSON<GroupsResponse>('/api/v1/groups'),
    enabled: open,
    retry: false,
  })
  const currentIds = new Set(currentGroups.map((group) => group.id))
  const options = (groupsQuery.data?.groups ?? []).filter((group) => !currentIds.has(group.id))
  const add = useMutation({
    mutationFn: () =>
      postJSON(`/api/v1/groups/${groupId}/members`, { member: { contact_id: contactId } }),
    onSuccess: async () => {
      await onAdded()
      onOpenChange(false)
    },
  })

  React.useEffect(() => {
    if (open) setGroupId('')
  }, [open])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Contact to Group</DialogTitle>
          <DialogDescription>Choose a hand-managed Group for this Contact.</DialogDescription>
        </DialogHeader>
        {add.isError && <Alert>Contact could not be added to the Group.</Alert>}
        {groupsQuery.isError && <Alert>Groups unavailable.</Alert>}
        {groupsQuery.isSuccess && options.length === 0 ? (
          <p className="text-body-sm text-muted-foreground">
            This Contact is already in every Group.
          </p>
        ) : (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="contact-group">Group</Label>
            <Select value={groupId} onValueChange={setGroupId}>
              <SelectTrigger id="contact-group">
                <SelectValue placeholder="Select a Group" />
              </SelectTrigger>
              <SelectContent>
                {options.map((group) => (
                  <SelectItem key={group.id} value={String(group.id)}>
                    {group.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={!groupId || add.isPending} onClick={() => add.mutate()}>
            {add.isPending ? 'Adding…' : 'Add to Group'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
